// Browser errors to the shared Velnex GlitchTip (project loop-coffee-web). The public DSN comes from
// runtime-config.js (infra/scripts/web-flag.sh), so reporting is switched per environment without a
// rebuild; without it the SDK is never downloaded. The release is the commit the build came from.
const browserRuntime = () => (typeof window === 'undefined' ? undefined : window.__LOOP_RUNTIME_CONFIG__);

const PUBLIC_DSN = /^https:\/\/[0-9a-f]{32}@[a-z0-9]([a-z0-9.-]*[a-z0-9])?\/[0-9]+$/;

export function readErrorReportingConfig(runtime = browserRuntime(), env = process.env) {
    const rt = runtime !== null && typeof runtime === 'object' && !Array.isArray(runtime) ? runtime : {};
    const dsn = typeof rt.glitchtipDsn === 'string' ? rt.glitchtipDsn.trim() : '';
    if (!PUBLIC_DSN.test(dsn)) return null;
    return {
        dsn,
        environment: typeof rt.glitchtipEnvironment === 'string' && rt.glitchtipEnvironment ? rt.glitchtipEnvironment : 'development',
        release: env.REACT_APP_RELEASE || undefined,
    };
}

// The server is shared and keeps what it receives: personal data is removed here. Same rules as
// apps/api/src/lib/error-scrub.ts (keys that name personal data, then patterns in every string).
export const FILTERED = '[Filtered]';

const SENSITIVE_KEY =
    /(^|_)(e_?mail|cpf|cnpj|document|tax_?id|phone|telefone|celular|mobile|whatsapp|password|senha|passwd|secret|token|api_?key|authorization|cookies?|session(_?id)?|address(_\d+)?|endereco|street|postal_?code|zip(_?code)?|cep|first_?name|last_?name|full_?name|customer_?name|holder_?name|recipient|birth(_?date)?|birthdate|card(_?number)?)(_|$)/;

// No lookbehind: older Safari fails to parse it, and a parse error would take the whole bundle down.
// The character before a match is captured and put back instead.
const PATTERNS = [
    [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
    [/(^|[^\w./])\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}(?![\w/])/g, '$1[cnpj]'],
    [/(^|[^\w./])\d{3}\.?\d{3}\.?\d{3}-?\d{2}(?![\w/])/g, '$1[cpf]'],
    [/(^|[^\w/])(?:\+?55[\s.-]?)?\(?\d{2}\)?[\s.-]?9?\d{4}[\s.-]?\d{4}(?![\w/])/g, '$1[phone]'],
];

const MAX_DEPTH = 12;

const isSensitiveKey = (key) =>
    SENSITIVE_KEY.test(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase().replace(/[^a-z0-9]+/g, '_'));

export const scrubString = (value) => PATTERNS.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), value);

function scrubValue(value, depth, seen) {
    if (typeof value === 'string') return scrubString(value);
    if (value === null || typeof value !== 'object') return value;
    if (seen.has(value) || depth > MAX_DEPTH) return FILTERED;
    seen.add(value);
    if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1, seen));
    const out = {};
    Object.entries(value).forEach(([key, item]) => {
        out[key] = isSensitiveKey(key) ? FILTERED : scrubValue(item, depth + 1, seen);
    });
    return out;
}

const withoutQuery = (url) => url.split(/[?#]/)[0];

export function scrubEvent(event) {
    const { user, request, ...rest } = event;
    const scrubbed = scrubValue(rest, 0, new WeakSet());
    if (request) {
        scrubbed.request = request.url ? { url: scrubString(withoutQuery(request.url)) } : {};
    }
    return scrubbed;
}

export function errorReportingOptions(config) {
    return {
        dsn: config.dsn,
        release: config.release,
        environment: config.environment,
        sendDefaultPii: false,
        // Only the default integrations: no browser tracing and no session replay.
        tracePropagationTargets: [],
        beforeSend: (event) => scrubEvent(event),
        beforeBreadcrumb: (breadcrumb) => scrubValue(breadcrumb, 0, new WeakSet()),
    };
}

/**
 * Starts reporting when the runtime config names a DSN. The SDK is a separate chunk loaded after
 * the page renders; a failure to load or to reach GlitchTip is swallowed, the page never notices.
 */
export async function initErrorReporting({ runtime = browserRuntime(), env = process.env, load = () => import('./errorReportingSdk') } = {}) {
    const config = readErrorReportingConfig(runtime, env);
    if (!config) return false;
    try {
        const Sentry = await load();
        Sentry.init(errorReportingOptions(config));
        return true;
    } catch {
        return false;
    }
}
